import React, { useEffect } from 'react';
import { compose } from 'utils/compose';
import { inject, observer } from 'mobx-react';
import { Box, DialogContent, DialogTitle, FormControlLabel, RadioGroup, Typography } from '@mui/material';

import Header from 'components/Header';
import withMediaQuery from 'constants/mediaQuery';
import menuItems from 'constants/menuItems';
import RadioButton from 'components/RadioButton';
import { DialogActionButton, DialogFooter } from 'components/Dialog';
import { VERIFICATION } from 'constants/verification';

import { convertLabelPodMethods, replaceSeparator } from 'utils/common';

import { useStyles } from '../styles';

function DialogStepOne({ store, mediaQuery, closeDialog, handleNext, handleClose, isDisplayMenu }) {
  const classes = useStyles();
  const { isRequested } = store.podStore;

  const handleChangeVerificationMethod = (e) => {
    if (!e.target?.value) return;

    store.deliveryStore.setVerificationMethod(e.target.value);
  };

  const nextStep = () => {
    if (typeof handleNext === 'function') {
      const { verificationMethod } = store.deliveryStore;
      // shipment-info (POD) methods don't send a code → go straight to the input step
      if (verificationMethod !== VERIFICATION.SMS && verificationMethod !== VERIFICATION.EMAIL) {
        handleNext(2);
        return;
      }
      store.deliveryStore.requestCode(() => handleNext(2));
      return;
    }
    store.deliveryStore.processNextStep();
  };

  const { delivery, dialogLoading } = store.deliveryStore;

  useEffect(() => {
    const verificationMethods = getVerificationMethods();
    if (delivery && delivery.settings?.allow_request_code !== 'false' && verificationMethods?.[0]) {
      store.deliveryStore.setVerificationMethod(verificationMethods[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [delivery, store.deliveryStore]);

  if (!delivery) return null;

  const { shipment, settings } = delivery;
  const allowRequestCode = settings.allow_request_code !== 'false';
  const enablePublicPod = settings?.enabled_verify_by_shipment_info === 'true' || false;
  const podVerificationMethods = settings?.pod_verify_methods;
  const { customer } = shipment;
  const { isPODFeedback } = store.podStore;

  const publicPodDisabled = !isPODFeedback || !enablePublicPod || !podVerificationMethods;

  const getVerificationMethods = () => {
    const verificationMethods = [];
    if (allowRequestCode && customer?.phone_number) {
      verificationMethods.push('sms');
    }
    if (allowRequestCode && customer?.email) {
      verificationMethods.push('email');
    }
    if (!publicPodDisabled && podVerificationMethods) {
      verificationMethods.push(...podVerificationMethods.split(','));
    }
    return verificationMethods;
  };

  const defaultVerificationMethod = getVerificationMethods()[0];
  const verificationAvailable = allowRequestCode && !!defaultVerificationMethod;

  const renderMessage = () => {
    // Case 1: delivery completed (>14 days) — request code disabled, nothing available
    if (!allowRequestCode) {
      return (
        <Box py={2}>
          <Typography color="primary">
            Delivery details are no longer available because the delivery has been completed. Please contact the merchant for additional information.
          </Typography>
        </Box>
      );
    }
    // Case 2: allowed, but no usable verification method (no phone, no email, no POD verify method)
    if (!defaultVerificationMethod) {
      return (
        <Box py={2}>
          <Typography color="primary">
            There is no verification method for the shipment -  please contact the merchant for additional information.
          </Typography>
        </Box>
      );
    }
    // Case 3: at least one verification method available — show options
    return (
      <Box>
        <Box py={1} mt={3} mb={2} sx={classes.dialogMethodSelectTitle}>
          <strong>Select a verification method</strong>
        </Box>
        <RadioGroup onChange={handleChangeVerificationMethod} defaultValue={defaultVerificationMethod} name="verify_type">
          {allowRequestCode && shipment.customer?.phone_number && (
            <FormControlLabel
              value="sms"
              control={<RadioButton />}
              sx={{ ...classes.radioLabelWrapper, '& .MuiFormControlLabel-label': { flex: 1 } }}
              label={
                <Box component="span" sx={{ ...classes.textOnPrimaryContainer, ...classes.radioLabel }}>
                  SMS
                </Box>
              }
            />
          )}
          {allowRequestCode && shipment.customer?.email && (
            <FormControlLabel
              value="email"
              control={<RadioButton />}
              sx={{ ...classes.radioLabelWrapper, '& .MuiFormControlLabel-label': { flex: 1 } }}
              label={
                <Box component="span" sx={{ ...classes.textOnPrimaryContainer, ...classes.radioLabel }}>
                  Email
                </Box>
              }
            />
          )}
          {enablePublicPod &&
            podVerificationMethods &&
            store.podStore.isPODFeedback &&
            podVerificationMethods.split(',').map((method) => {
              const formatMethodLabel = (methodStr) => {
                if (methodStr.includes('|')) {
                  return methodStr
                    .split('|')
                    .map((part) => {
                      return convertLabelPodMethods(part);
                    })
                    .join('&');
                }
                return convertLabelPodMethods(methodStr);
              };

              return (
                <FormControlLabel
                  key={method}
                  value={method}
                  control={<RadioButton />}
                  sx={{ ...classes.radioLabelWrapper, '& .MuiFormControlLabel-label': { flex: 1 } }}
                  label={
                    <Box component="span" sx={{ ...classes.textOnPrimaryContainer, ...classes.radioLabel }}>
                      {`Verify with your ${replaceSeparator(formatMethodLabel(method))}`}
                    </Box>
                  }
                />
              );
            })}
        </RadioGroup>
      </Box>
    );
  };

  return (
    <Box>
      {mediaQuery.isMobile && isDisplayMenu && <Header menuItems={menuItems} />}
      <DialogTitle>
        {!verificationAvailable ? (
          <strong>Delivery Details Unavailable</strong>
        ) : isRequested ? (
          <strong>Request POD</strong>
        ) : (
          <strong>Please help us to verify it's you!</strong>
        )}
      </DialogTitle>
      <DialogContent>
        {verificationAvailable && (
          <Typography sx={classes.darkGrayText}>We will send a verification code to your preferred means of contact to make sure it's you!</Typography>
        )}
        {renderMessage()}
      </DialogContent>
      <DialogFooter row>
        <DialogActionButton
          onClick={() => {
            closeDialog?.();
            handleClose?.();
            store.isViewPins = false;
          }}
          variant="outlined"
          color="primary"
          disableElevation
        >
          Cancel
        </DialogActionButton>
        {verificationAvailable && (
          <DialogActionButton onClick={nextStep} disabled={dialogLoading} variant="contained" color="primary" disableElevation>
            Next
          </DialogActionButton>
        )}
      </DialogFooter>
    </Box>
  );
}

const DialogStepOneCompose = compose(inject('store'), observer)(DialogStepOne);

export default withMediaQuery()(DialogStepOneCompose);
